import { Box, Container, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';

import { App, Page } from '@/types/generated';
import RichText, { RichTextContent } from '@/components/RichText/RichText';
import ImageWrapper from '@/components/ImageWrapper';
import BreadCrumbs from '@/components/BreadCrumbs';
import FooterBanner from '@/components/FooterBanner';
import TAGLink from '@/components/TAGLink';

import {
	getContentWrapperStyles,
	copyrightStyles,
	getWrapperStyles,
	breadCrumbsContainerStyles,
	getAboutWrapperStyles,
	breadCrumbsAndTopBtnContainerStyles,
	getFooterWrapperStyles,
	getFirstRowStyles,
	getSecondRowStyles,
	getThirdRowStyles,
} from './Footer.styles';
import FooterNavigationContent from './FooterNavigationContent';
import FooterPolicy from './FooterPolicy';

export interface IFooter {
	/**
	 * Application footer data, containing the information about the brand and the navigation links
	 *
	 * @type {App['footer']}
	 * @memberof IFooter
	 */
	footerData: App['footer'];
	/**
	 * Whether the footer should display the scheduling content or not
	 *
	 * @type {boolean}
	 * @memberof IFooter
	 */
	withScheduling?: boolean;
	/**
	 * breadcrumbsData to display breadcrumbs
	 *
	 * @type {boolean}
	 * @memberof IFooter
	 */
	breadcrumbsData?: Page;
	/**
	 * Sets the Footer background color to light if true, otherwise dark.
	 *
	 * @type {boolean}
	 * @memberof IFooter
	 */
	isLightFooter?: boolean;
}

export default function Footer({
	footerData,
	withScheduling = false,
	breadcrumbsData,
	isLightFooter = false,
}: Readonly<IFooter>) {
	const theme = useTheme();
	const isSmallDevice = useMediaQuery(theme.breakpoints.down('md'));

	return (
		<Box component="footer" data-testid="section_footer" sx={{ ...getWrapperStyles() }}>
			{!withScheduling && (
				<Grid container sx={breadCrumbsAndTopBtnContainerStyles}>
					{breadcrumbsData?.breadcrumbsCollection && (
						<Grid item sx={breadCrumbsContainerStyles}>
							<BreadCrumbs breadcrumbsData={breadcrumbsData} isFooterLite={isLightFooter} />
						</Grid>
					)}
				</Grid>
			)}

			<Container sx={getContentWrapperStyles} maxWidth={false}>
				<Box sx={getFooterWrapperStyles}>
					{/* first row in footer */}
					<Box sx={getFirstRowStyles}>
						{/* Adjust width as needed */}
						<Box sx={{ mb: 2 }}>
							<TAGLink href="/">
								<ImageWrapper
									src={footerData?.logo?.url}
									width="100%"
									height="unset"
									data-testid="img_logo_desktop_with_nav_menu"
									alt="brand logo"
								/>
							</TAGLink>
						</Box>
						{isSmallDevice && (
							<Box
								width={'100%'}
								height={'1px'}
								bgcolor={'#FFF'}
								mb={isSmallDevice ? '2.5rem' : '0px'}
							></Box>
						)}
						<Box sx={{ flexGrow: 0, width: { xs: '100%', md: '40%' }, marginTop: { md: '17px' } }}>
							<FooterNavigationContent
								navigationGroups={footerData?.navigationGroupsCollection}
								isLightFooter={isLightFooter}
							/>
						</Box>
						{isSmallDevice && <Box width={'100%'} height={'1px'} bgcolor={'#FFF'}></Box>}
					</Box>
					<Box sx={getSecondRowStyles}>
						{' '}
						{/* Adjust width as needed */}
						{footerData?.about?.json && (
							<Container
								data-testid="section_footer_about"
								sx={{
									...getAboutWrapperStyles,
									flexDirection: 'column',
									gap: '.625rem',
									marginBottom: '24px',
								}}
							>
								<RichText
									content={footerData.about.json as RichTextContent}
									docProps={{
										color: isLightFooter ? 'text.primary' : 'text.light',
										fontSize: { xs: '1.5rem', md: '1.5rem' },
										fontWeight: 300,
										fontFamily: 'Messina Modern',
									}}
								/>
							</Container>
						)}
						{footerData?.aboutDescription?.json && (
							<Container
								data-testid="section_footer_aboutDescription"
								sx={{
									...getAboutWrapperStyles,
									flexDirection: 'column',
									gap: '1rem',
									width: { xs: '100%', md: '66%' },
								}}
							>
								<RichText
									content={footerData.aboutDescription.json as RichTextContent}
									docProps={{
										color: '#A8A8A8',
										fontSize: { xs: '.75rem', md: '.63vw' },
										fontWeight: 350,
										fontFamily: 'Messina Sans',
										lineHeight: '1.2rem',
									}}
								/>
							</Container>
						)}
					</Box>
					<Box sx={getThirdRowStyles}>
						<Typography
							variant="bodySmallBook"
							color={isLightFooter ? 'text.primary' : 'text.light'}
							sx={copyrightStyles}
							data-testid="text_footer_copyright"
						>
							{footerData?.copyright || ''}
						</Typography>

						{withScheduling && !!footerData?.footerPolicyGroupCollection?.items?.length ? (
							<FooterPolicy policyGroup={footerData?.footerPolicyGroupCollection} />
						) : null}
					</Box>
				</Box>
			</Container>
			<FooterBanner />
		</Box>
	);
}
